import styled from "styled-components";

import { pageRoot } from "../../styles/pageRoot";
import {
  fontSizes,
  lineHeights,
  sectionTitle,
} from "../../shared/styles/typography";

export const GamePageRoot = styled.div`
  ${pageRoot}
  padding: 16px;
  gap: 16px;
  width: 100%;
`;

export const GameTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const GameTitle = styled.h1`
  ${sectionTitle}
  margin: 0;
`;

export const ScoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primaryAlpha10};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${fontSizes.sm}px;
`;

export const ProgressText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: 400;
`;

export const MediaBox = styled.div`
  width: 100%;
  max-width: 420px;
  aspect-ratio: 16 / 10;
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;

  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  width: 100%;
  max-width: 420px;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const OptionBtn = styled.button<{
  $state?: "idle" | "correct" | "wrong" | "disabled";
}>`
  padding: 14px 16px;
  border-radius: 14px;
  border: 2px solid
    ${({ $state, theme }) => {
      if ($state === "correct") return theme.colors.success;
      if ($state === "wrong") return theme.colors.danger;
      return theme.colors.border;
    }};
  background: ${({ $state, theme }) => {
    if ($state === "correct") return "rgba(16,185,129,0.12)";
    if ($state === "wrong") return "rgba(239,68,68,0.12)";
    return theme.colors.surface;
  }};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${fontSizes.base}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: ${({ $state }) => ($state === "disabled" ? "default" : "pointer")};
  text-align: center;
  transition: transform 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: ${({ $state }) => ($state === "idle" ? 0.5 : 1)};
  }
`;

export const Feedback = styled.div<{ $ok?: boolean }>`
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ $ok }) =>
    $ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)"};
  color: ${({ $ok, theme }) =>
    $ok ? theme.colors.success : theme.colors.danger};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  width: 100%;
  max-width: 420px;
`;

export const CompleteCard = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AnswerInput = styled.input`
  width: 100%;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${fontSizes.base}px;
  letter-spacing: 2px;
  text-align: center;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 480px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const MemoryCard = styled.button<{
  $flipped?: boolean;
  $matched?: boolean;
}>`
  aspect-ratio: 1;
  border-radius: 12px;
  border: 2px solid
    ${({ $matched, theme }) =>
      $matched ? theme.colors.success : theme.colors.border};
  background: ${({ $flipped, $matched, theme }) => {
    if ($matched) return "rgba(16,185,129,0.15)";
    if ($flipped) return theme.colors.surface;
    return theme.colors.primary;
  }};
  color: ${({ $flipped, theme }) =>
    $flipped ? theme.colors.text : theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  overflow: hidden;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${fontSizes.base}px;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

export const MatchColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  max-width: 520px;
`;

export const MatchItem = styled.button<{
  $selected?: boolean;
  $done?: boolean;
}>`
  padding: 12px;
  border-radius: 12px;
  border: 2px solid
    ${({ $selected, $done, theme }) => {
      if ($done) return theme.colors.success;
      if ($selected) return theme.colors.primary;
      return theme.colors.border;
    }};
  background: ${({ $done, theme }) =>
    $done ? "rgba(16,185,129,0.12)" : theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: ${({ $done }) => ($done ? "default" : "pointer")};
  text-align: left;
  font-size: ${fontSizes.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;
